import React, { useState, useEffect } from 'react';
import { API } from '../../services/api';
import ConfirmDialog from '../signatures/ConfirmDialog';
import TemplateVersionPreviewModal from './TemplateVersionPreviewModal';
import './TemplateVersionHistoryModal.css';

function TemplateVersionHistoryModal({ isOpen, templateId, templateName, onClose, onRestore, canUpdate = true }) {
    const [versions, setVersions] = useState([]);
    const [currentTemplate, setCurrentTemplate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedVersion, setSelectedVersion] = useState(null);
    const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
    const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [previewVersion, setPreviewVersion] = useState(null);
    const [restoring, setRestoring] = useState(false);
    const [cascadeResults, setCascadeResults] = useState(null);

    useEffect(() => {
        if (isOpen && templateId) {
            loadData();
        }
    }, [isOpen, templateId]);

    const loadData = async () => {
        try {
            setLoading(true);
            setError('');
            setCascadeResults(null);

            // Charger le template actuel et les versions
            const [templateResponse, versionsResponse] = await Promise.all([
                API.getTemplate(templateId),
                API.getTemplateVersions(templateId)
            ]);

            setCurrentTemplate(templateResponse.data.template);
            setVersions(versionsResponse.data.versions || []);
        } catch (err) {
            console.error('Load data error:', err);
            setError('Erreur lors du chargement des versions');
        } finally {
            setLoading(false);
        }
    };

    const handleRestoreClick = (version) => {
        setSelectedVersion(version);
        setShowRestoreConfirm(true);
        setCascadeResults(null);
    };

    const handleConfirmRestore = async () => {
        if (!selectedVersion) return;

        try {
            setRestoring(true);
            const response = await API.restoreTemplateVersion(templateId, selectedVersion.id);

            // Afficher les résultats du cascade update
            if (response && response.data) {
                if (response.data.cascade_update) {
                    setCascadeResults(response.data.cascade_update);
                }

                // Recharger les versions
                await loadData();

                // Si pas de cascade ou cascade terminé, fermer le modal après 2 secondes
                if (!response.data.cascade_update || response.data.cascade_update.updated === 0) {
                    setTimeout(() => {
                        setShowRestoreConfirm(false);
                        setSelectedVersion(null);

                        // Callback pour recharger la liste des templates
                        if (onRestore) {
                            onRestore();
                        }

                        // Fermer la modal
                        onClose();
                    }, 2000);
                }
            }
        } catch (err) {
            console.error('Restore error:', err);
            setError('Erreur lors de la restauration: ' + err.message);
        } finally {
            setRestoring(false);
        }
    };

    const handleDuplicateClick = (version) => {
        setSelectedVersion(version);
        setShowDuplicateConfirm(true);
    };

    const handleConfirmDuplicate = async () => {
        if (!selectedVersion) return;

        try {
            setLoading(true);
            // Créer un nouveau template basé sur cette version
            const versionData = await API.getTemplateVersion(templateId, selectedVersion.id);
            const newTemplate = {
                name: `${versionData.data.version.name} (copie v${selectedVersion.version_number})`,
                description: currentTemplate.description,
                grid_data: versionData.data.version.grid_data,
                container_width: versionData.data.version.container_width,
                category: currentTemplate.category,
                is_public: false
            };

            await API.createTemplate(newTemplate);
            setShowDuplicateConfirm(false);
            setSelectedVersion(null);

            // Callback pour recharger la liste des templates
            if (onRestore) {
                onRestore();
            }

            // Fermer la modal
            onClose();
        } catch (err) {
            console.error('Duplicate error:', err);
            setError('Erreur lors de la duplication');
        } finally {
            setLoading(false);
        }
    };

    const handlePreviewClick = (version) => {
        setPreviewVersion(version);
        setShowPreview(true);
    };

    const handlePreviewCurrent = () => {
        // Créer un objet version-like à partir du template actuel
        const currentAsVersion = {
            id: currentTemplate.id,
            version_number: 'actuelle',
            name: currentTemplate.name,
            grid_data: currentTemplate.grid_data,
            container_width: currentTemplate.container_width,
            created_at: currentTemplate.updated_at
        };
        setPreviewVersion(currentAsVersion);
        setShowPreview(true);
    };

    const handleDuplicateCurrent = async () => {
        try {
            setLoading(true);
            await API.duplicateTemplate(templateId);

            // Callback pour recharger la liste des templates
            if (onRestore) {
                onRestore();
            }

            // Fermer la modal
            onClose();
        } catch (err) {
            console.error('Duplicate current error:', err);
            setError('Erreur lors de la duplication');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="version-modal-overlay" onClick={onClose}>
                <div className="version-modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="version-modal-header">
                        <h2>Historique des versions</h2>
                        <p className="version-modal-subtitle">{templateName}</p>
                        <button className="version-modal-close" onClick={onClose}>
                            <svg
                                width="20"
                                height="20"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>

                    <div className="version-modal-body">
                        {error && (
                            <div className="version-error">{error}</div>
                        )}

                        {loading ? (
                            <div className="version-loading">
                                <div className="spinner"></div>
                                <p>Chargement des versions...</p>
                            </div>
                        ) : (
                            <div className="version-list">
                                {/* Version actuelle */}
                                {currentTemplate && (
                                    <div className="version-item version-item-current">
                                        <div className="version-item-header">
                                            <div className="version-item-number">
                                                Version actuelle
                                            </div>
                                            <div className="version-item-date">
                                                {formatDate(currentTemplate.updated_at)}
                                            </div>
                                        </div>

                                        <div className="version-item-body">
                                            <div className="version-item-info">
                                                <div className="version-item-name">
                                                    {currentTemplate.name}
                                                </div>
                                                <div className="version-item-meta">
                                                    <span className="version-badge version-badge-current">
                                                        En cours d'utilisation
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="version-item-actions">
                                                <button
                                                    className="version-item-preview-btn"
                                                    onClick={handlePreviewCurrent}
                                                    disabled={loading}
                                                >
                                                    <svg
                                                        width="16"
                                                        height="16"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                                        />
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                                        />
                                                    </svg>
                                                    Aperçu
                                                </button>
                                                {canUpdate && (
                                                    <button
                                                        className="version-item-duplicate-btn"
                                                        onClick={handleDuplicateCurrent}
                                                        disabled={loading}
                                                    >
                                                        <svg
                                                            width="16"
                                                            height="16"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                                            />
                                                        </svg>
                                                        Dupliquer
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Message si aucune version sauvegardée */}
                                {versions.length === 0 && (
                                    <div className="version-empty-inline">
                                        <svg
                                            width="24"
                                            height="24"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={1.5}
                                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                            />
                                        </svg>
                                        <p>Aucune version antérieure sauvegardée</p>
                                    </div>
                                )}

                                {/* Versions précédentes */}
                                {versions.map((version) => (
                                    <div key={version.id} className="version-item">
                                        <div className="version-item-header">
                                            <div className="version-item-number">
                                                Version {version.version_number}
                                            </div>
                                            <div className="version-item-date">
                                                {formatDate(version.created_at)}
                                            </div>
                                        </div>

                                        <div className="version-item-body">
                                            <div className="version-item-info">
                                                <div className="version-item-name">
                                                    {version.name || currentTemplate.name}
                                                </div>
                                                {version.comment && (
                                                    <div className="version-item-comment">
                                                        {version.comment}
                                                    </div>
                                                )}
                                                <div className="version-item-meta">
                                                    {version.is_auto_save ? (
                                                        <span className="version-badge version-badge-auto">
                                                            Sauvegarde automatique
                                                        </span>
                                                    ) : (
                                                        <span className="version-badge version-badge-manual">
                                                            Sauvegarde manuelle
                                                        </span>
                                                    )}
                                                    <span className="version-item-author">
                                                        par {version.created_by}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="version-item-actions">
                                                <button
                                                    className="version-item-preview-btn"
                                                    onClick={() => handlePreviewClick(version)}
                                                    disabled={loading}
                                                >
                                                    <svg
                                                        width="16"
                                                        height="16"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                                        />
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                                        />
                                                    </svg>
                                                    Aperçu
                                                </button>
                                                {canUpdate && (
                                                    <button
                                                        className="version-item-restore-btn"
                                                        onClick={() => handleRestoreClick(version)}
                                                        disabled={loading}
                                                    >
                                                        <svg
                                                            width="16"
                                                            height="16"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                                            />
                                                        </svg>
                                                        Restaurer
                                                    </button>
                                                )}
                                                {canUpdate && (
                                                    <button
                                                        className="version-item-duplicate-btn"
                                                        onClick={() => handleDuplicateClick(version)}
                                                        disabled={loading}
                                                    >
                                                        <svg
                                                            width="16"
                                                            height="16"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                                            />
                                                        </svg>
                                                        Dupliquer
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <ConfirmDialog
                isOpen={showRestoreConfirm && !cascadeResults}
                title="Restaurer cette version"
                message={`Voulez-vous restaurer la version ${selectedVersion?.version_number} ? La version actuelle sera sauvegardée avant la restauration. Cette action va également mettre à jour toutes les signatures qui utilisent ce modèle.`}
                confirmText="Restaurer"
                cancelText="Annuler"
                onConfirm={handleConfirmRestore}
                onCancel={() => {
                    setShowRestoreConfirm(false);
                    setSelectedVersion(null);
                }}
                danger={false}
                loading={restoring}
            />

            {/* Modal de résultats cascade */}
            {showRestoreConfirm && cascadeResults && (
                <div className="modal-overlay" onClick={() => {
                    setShowRestoreConfirm(false);
                    setSelectedVersion(null);
                    setCascadeResults(null);
                    if (onRestore) onRestore();
                    onClose();
                }}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>Restauration terminée</h2>

                        <div className="cascade-results">
                            <h3>✅ Template restauré</h3>
                            <p>
                                <strong>{cascadeResults.updated}</strong> signature(s) mise(s) à jour
                            </p>
                            {cascadeResults.locked > 0 && (
                                <p className="locked-info">
                                    🔒 {cascadeResults.locked} signature(s) verrouillée(s) (non modifiée(s))
                                </p>
                            )}
                            {cascadeResults.failed > 0 && (
                                <p className="error-info">
                                    ❌ {cascadeResults.failed} signature(s) en erreur
                                </p>
                            )}
                        </div>

                        <div className="modal-actions">
                            <button
                                className="btn btn-primary"
                                onClick={() => {
                                    setShowRestoreConfirm(false);
                                    setSelectedVersion(null);
                                    setCascadeResults(null);
                                    if (onRestore) onRestore();
                                    onClose();
                                }}
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={showDuplicateConfirm}
                title="Dupliquer cette version"
                message={`Voulez-vous créer une copie de la version ${selectedVersion?.version_number} ? Un nouveau modèle sera créé.`}
                confirmText="Dupliquer"
                cancelText="Annuler"
                onConfirm={handleConfirmDuplicate}
                onCancel={() => {
                    setShowDuplicateConfirm(false);
                    setSelectedVersion(null);
                }}
                danger={false}
            />

            <TemplateVersionPreviewModal
                isOpen={showPreview}
                version={previewVersion}
                onClose={() => {
                    setShowPreview(false);
                    setPreviewVersion(null);
                }}
            />
        </>
    );
}

export default TemplateVersionHistoryModal;
